import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy } from "@aurumiq/ui/lib/format";
import { Ban, Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { usePermission } from "@/auth/use-permission";
import { QueryState, Stat, StatList } from "@/components/query-state";
import { errorMessage } from "@/lib/api";
import type { License, Subscription } from "@/lib/types";
import { useOrgLicenses, useOrgSubscription, useRevokeLicense } from "./queries";
import { SubscriptionDialog } from "./subscription-dialog";

const col = createColumnHelper<License>();

export function SubscriptionCard({
  orgId,
  sub,
  canWrite,
}: {
  orgId: string;
  sub: Subscription | null;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);
  const pctUsed =
    sub && sub.seats > 0 ? Math.min(100, Math.round((sub.seats_used / sub.seats) * 100)) : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Subscription
          {sub ? (
            <StatusTag variant={statusVariant(sub.status)}>{sub.status.toUpperCase()}</StatusTag>
          ) : null}
        </CardTitle>
        {canWrite ? (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Pencil /> Change plan / seats
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {sub ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <StatList>
              <Stat label="Plan">
                {sub.plan_name}{" "}
                <span className="font-mono text-muted-foreground text-xs">{sub.plan_code}</span>
              </Stat>
              <Stat label="Interval">{sub.interval ?? "—"}</Stat>
              <Stat label="Period end">
                {sub.current_period_end ? dmy(sub.current_period_end) : "—"}
              </Stat>
              <Stat label="Subscribed until">{sub.sub_until ? dmy(sub.sub_until) : "—"}</Stat>
            </StatList>
            <div>
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-muted-foreground">Seats</span>
                <span className="tabular">
                  {sub.seats_used} / {sub.seats}
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuenow={sub.seats_used}
                aria-valuemin={0}
                aria-valuemax={sub.seats}
                aria-label="Seats used"
              >
                <div
                  className={pctUsed >= 100 ? "h-full bg-danger" : "h-full bg-gold"}
                  style={{ width: `${pctUsed}%` }}
                />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            No subscription on file for this organisation.
          </p>
        )}
      </CardContent>
      <SubscriptionDialog orgId={orgId} current={sub} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

export function LicensesTab({ orgId }: { orgId: string }) {
  const canWrite = usePermission("licenses:write");
  const canOrgWrite = usePermission("orgs:write");
  const sub = useOrgSubscription(orgId);
  const [page, setPage] = useState(1);
  const licenses = useOrgLicenses(orgId, page);
  const revoke = useRevokeLicense(orgId);
  const [revoking, setRevoking] = useState<License | null>(null);

  const columns = useMemo(
    () => [
      col.accessor("device_name", {
        header: "Device",
        cell: (c) => (
          <div>
            <div className="font-semibold">{c.getValue()}</div>
            <div className="text-muted-foreground text-xs">{c.row.original.platform}</div>
          </div>
        ),
      }),
      col.accessor("tier", {
        header: "Tier",
        cell: (c) => (
          <StatusTag variant={statusVariant(c.getValue())}>{c.getValue().toUpperCase()}</StatusTag>
        ),
      }),
      col.accessor("kid", {
        header: "Key ID",
        cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
      }),
      col.accessor("seats", { header: "Seats", meta: { numeric: true } }),
      col.accessor("issued_at", { header: "Issued", cell: (c) => dmy(c.getValue()) }),
      col.accessor("expires_at", { header: "Expires", cell: (c) => dmy(c.getValue()) || "—" }),
      col.accessor("sub_until", { header: "Sub until", cell: (c) => dmy(c.getValue()) || "—" }),
      col.accessor("revoked_at", {
        header: "Status",
        cell: (c) =>
          c.getValue() ? (
            <span title={c.row.original.revoke_reason ?? undefined}>
              <StatusTag variant="err">REVOKED</StatusTag>{" "}
              <span className="text-muted-foreground text-xs">{dmy(c.getValue())}</span>
            </span>
          ) : c.row.original.is_current ? (
            <StatusTag variant="ok">CURRENT</StatusTag>
          ) : (
            <StatusTag>SUPERSEDED</StatusTag>
          ),
      }),
      col.display({
        id: "actions",
        header: "",
        meta: { numeric: true },
        cell: (c) =>
          canWrite && c.row.original.is_current && !c.row.original.revoked_at ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              onClick={() => setRevoking(c.row.original)}
              aria-label={`Revoke licence ${c.row.original.id}`}
            >
              <Ban /> Revoke
            </Button>
          ) : null,
      }),
    ],
    [canWrite],
  );

  const total = licenses.data?.total ?? 0;
  const pageSize = licenses.data?.page_size ?? 25;

  return (
    <div className="grid gap-4">
      <QueryState query={sub} rows={2}>
        {(s) => <SubscriptionCard orgId={orgId} sub={s} canWrite={canOrgWrite} />}
      </QueryState>
      <DataTable
        columns={columns as ColumnDef<License, unknown>[]}
        data={licenses.data?.items ?? []}
        isLoading={licenses.isPending}
        getRowId={(l) => l.id}
        pageCount={Math.max(1, Math.ceil(total / pageSize))}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={(p) => setPage(p.pageIndex + 1)}
        pageSizeOptions={[pageSize]}
        emptyMessage={
          licenses.isError
            ? `Could not load licences: ${errorMessage(licenses.error)}`
            : "No licences issued yet."
        }
      />
      <ReasonDialog
        open={revoking !== null}
        onOpenChange={(o) => {
          if (!o) setRevoking(null);
        }}
        title={`Revoke licence for ${revoking?.device_name}?`}
        description="The device drops to read-only at its next check-in. A new licence is issued only after the device re-activates."
        confirmLabel="Revoke licence"
        onConfirm={async (reason) => {
          if (revoking) await revoke.mutateAsync({ id: revoking.id, reason });
        }}
      />
    </div>
  );
}
